import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export default function Investors() {
    return (
        <main className="bg-gradient-to-b from-slate-900 to-black text-gray-100 py-16 overflow-hidden">
            {/* ---------------- HERO ---------------- */}
            <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                >
                    <img
                        src="/Images/logo-navbar.png"
                        alt="SecureID Logo"
                        className="mx-auto w-20 h-auto mb-6"
                    />
                    <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">
                        SecureID Technologies – Investor Relations
                    </h1>
                    <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        SecureID Technologies LLC is a growing provider of secure identity and testing
                        technology solutions for government, education, and enterprise clients.
                        Our platforms—ExamsPro, DactylScan, and Background Check Portal—are redefining
                        how organizations authenticate, assess, and manage trust.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className="mt-10"
                >
                    <img
                        src="/Images/investors-hero.jpg"
                        alt="SecureID Investor Overview"
                        className="mx-auto rounded-2xl shadow-2xl border border-slate-800 w-full max-w-4xl"
                    />
                </motion.div>
            </section>

            {/* ---------------- INVESTMENT HIGHLIGHTS ---------------- */}
            <section className="mt-20 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-white text-center mb-10">
                    SecureID Investment Highlights
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 text-gray-300">
                    {[
                        "Trusted provider of secure identity and testing technology.",
                        "Multi-product portfolio: ExamsPro, DactylScan, and Background Check Portal.",
                        "High customer retention through SaaS and recurring contracts.",
                        "Expanding partnerships with education, HR, and law enforcement sectors.",
                        "Strong alignment with biometric and AI-based authentication trends.",
                        "Lean, scalable model built for sustained growth and compliance.",
                    ].map((point, i) => (
                        <motion.div
                            key={i}
                            className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-indigo-500 transition"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                        >
                            <p className="text-sm leading-relaxed">{point}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ---------------- LATEST NEWS ---------------- */}
            <section className="mt-24 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-white text-center mb-10">
                    Latest News
                </h2>
                <div className="grid md:grid-cols-3 gap-6 text-gray-200">
                    {[
                        {
                            date: "Oct 10, 2025",
                            title: "SecureID Technologies Launches ExamsPro 2.0 with AI Blueprinting Assistant",
                        },
                        {
                            date: "Sep 22, 2025",
                            title: "SecureID Expands Strategic Partnership with Law Enforcement Agencies",
                        },
                        {
                            date: "Aug 12, 2025",
                            title: "SecureID Receives Florida Innovation Grant for Biometric Compliance Research",
                        },
                    ].map((n, i) => (
                        <motion.div
                            key={i}
                            className="bg-slate-800/40 p-5 rounded-xl border border-slate-700 hover:border-indigo-500 transition"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                        >
                            <p className="text-sm text-indigo-400 font-semibold mb-1">{n.date}</p>
                            <p className="text-base font-medium text-gray-100">{n.title}</p>
                        </motion.div>
                    ))}
                </div>
                <p className="text-center mt-8">
                    <Link
                        to="/news"
                        className="text-indigo-400 hover:underline text-sm font-medium"
                    >
                        View All News →
                    </Link>
                </p>
            </section>

            {/* ---------------- UPCOMING EVENTS ---------------- */}
            <section className="mt-24 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-white text-center mb-10">
                    Upcoming Events
                </h2>
                <div className="grid md:grid-cols-2 gap-6 text-gray-200">
                    {[
                        {
                            date: "Nov 15, 2025 at 10:00 AM EST",
                            title: "Investor Webinar: SecureID 2026 Roadmap & Growth Strategy",
                        },
                        {
                            date: "Dec 03, 2025 at 9:00 AM EST",
                            title: "SecureID Annual Innovation Briefing",
                        },
                    ].map((e, i) => (
                        <motion.div
                            key={i}
                            className="bg-slate-800/40 p-5 rounded-xl border border-slate-700 hover:border-indigo-500 transition"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                        >
                            <p className="text-sm text-indigo-400 font-semibold mb-1">{e.date}</p>
                            <p className="text-base font-medium text-gray-100">{e.title}</p>
                        </motion.div>
                    ))}
                </div>
                <p className="text-center mt-8">
                    <Link
                        to="/events"
                        className="text-indigo-400 hover:underline text-sm font-medium"
                    >
                        View All Events →
                    </Link>
                </p>
            </section>

            {/* ---------------- CONTACTS ---------------- */}
            <section className="mt-24 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-3xl font-bold text-white mb-8">Investor Contacts</h2>
                <div className="grid sm:grid-cols-2 gap-8 text-gray-300">
                    <div className="bg-slate-800/40 rounded-xl p-6 border border-slate-700">
                        <h3 className="text-lg font-semibold text-white">Investor Relations</h3>
                        <p className="mt-2 text-sm">
                            <a
                                href="mailto:investors@secureidtechnologies.com"
                                className="text-indigo-400 hover:underline"
                            >
                                investors@secureidtechnologies.com
                            </a>
                        </p>
                    </div>
                    <div className="bg-slate-800/40 rounded-xl p-6 border border-slate-700">
                        <h3 className="text-lg font-semibold text-white">Media Contact</h3>
                        <p className="mt-2 text-sm">
                            <a
                                href="mailto:press@secureidtechnologies.com"
                                className="text-indigo-400 hover:underline"
                            >
                                press@secureidtechnologies.com
                            </a>
                        </p>
                    </div>
                </div>
                <p className="mt-10 text-sm text-gray-500">
                    © {new Date().getFullYear()} SecureID Technologies LLC — All Rights Reserved.
                </p>
                <p className="mt-4 text-center text-sm text-gray-400">
                    <Link to="/" className="text-indigo-400 hover:underline">
                        ← Back to Home
                    </Link>
                </p>
            </section>
        </main>
    );
}
